import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getAnalysis, API } from "@/lib/api";

/**
 * Print-optimised report route. Fetches the same analysis, renders the
 * server-side Markdown (via /export/markdown) as a large scrollable page,
 * then triggers window.print(). No sidebar, no chrome.
 */
export default function Print() {
  const { id } = useParams();
  const { data: a } = useQuery({
    queryKey: ["analysis", id],
    queryFn: () => getAnalysis(id),
  });

  const [md, setMd] = React.useState("");

  useEffect(() => {
    if (!id) return;
    fetch(`${API}/analyses/${id}/export/markdown`)
      .then((r) => (r.ok ? r.text() : ""))
      .then(setMd)
      .catch(() => setMd(""));
  }, [id]);

  useEffect(() => {
    if (md) {
      const t = setTimeout(() => window.print(), 500);
      return () => clearTimeout(t);
    }
  }, [md]);

  if (!a) {
    return <div className="p-10 font-mono text-sm text-zinc-500">Loading print view…</div>;
  }

  return (
    <div className="min-h-screen bg-white text-zinc-950 p-10 max-w-4xl mx-auto print:p-0" data-testid="print-page">
      <style>{`
        @media print {
          @page { size: A4; margin: 18mm; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .no-print { display: none !important; }
        }
        .print-md { font-family: 'IBM Plex Sans', 'Helvetica', sans-serif; font-size: 11pt; line-height: 1.55; white-space: pre-wrap; }
      `}</style>
      <div className="no-print mb-6 flex items-center gap-3">
        <button
          onClick={() => window.print()}
          data-testid="trigger-print-button"
          className="bg-zinc-950 text-white px-4 py-2 font-mono text-xs uppercase tracking-widest hover:bg-zinc-800"
        >
          Print now
        </button>
        <span className="font-mono text-xs text-zinc-500">
          Tip: choose "Save as PDF" in the print dialog for a client-side PDF.
        </span>
      </div>
      <pre className="print-md">{md}</pre>
    </div>
  );
}
